import React from 'react';
import { Link } from 'react-router-dom';
import { resolveIcon } from '../../lib/toolLandingConfigs';
import { HelpCircle, ArrowRight } from 'lucide-react';

export default function ToolLandingSections({ config }) {
  if (!config) return null;

  return (
    <div className="space-y-12">
      {/* Features */}
      {config.features?.length > 0 && (
        <section aria-labelledby="features-heading">
          <h2 id="features-heading" className="text-xl font-bold text-foreground mb-6">
            Features &amp; Benefits
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {config.features.map((feature) => {
              const Icon = resolveIcon(feature.icon);
              return (
                <div key={feature.title} className="p-5 rounded-xl bg-white border border-border">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* How It Works */}
      {config.howItWorks?.length > 0 && (
        <section aria-labelledby="how-it-works-heading">
          <h2 id="how-it-works-heading" className="text-xl font-bold text-foreground mb-6">
            How It Works
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {config.howItWorks.map((step) => (
              <div key={step.step} className="relative p-5 rounded-xl bg-white border border-border">
                <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold mb-3">
                  {step.step}
                </div>
                <h3 className="font-semibold text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{step.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {config.faqs?.length > 0 && (
        <section aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {config.faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-xl bg-white border border-border p-4 open:shadow-sm"
              >
                <summary className="font-medium text-foreground cursor-pointer list-none flex items-center justify-between gap-2">
                  {faq.question}
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90 shrink-0" />
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related Tools */}
      {config.relatedTools?.length > 0 && (
        <section aria-labelledby="related-tools-heading">
          <h2 id="related-tools-heading" className="text-xl font-bold text-foreground mb-6">
            Related Tools
          </h2>
          <div className="flex flex-wrap gap-3">
            {config.relatedTools.map((tool) => (
              <Link
                key={tool.slug}
                to={`/services/${tool.slug}`}
                className="group inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground border-l-[3px] border-l-[#F47518] shadow-[0_2px_6px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.06)]"
              >
                {tool.label}
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
